import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 114 Backoffice Footstep History read
// API (BackofficeFootstepController/FootstepService). Same base/auth
// convention as ../tracking/trackingApi.js - apiClient.get with no `base`
// override, the same Bearer-session-authenticated backend every other
// backoffice controller uses. This file never calls a
// geofence/fake-location/face-recognition/SOS/export API (none of those
// exist anywhere in this codebase), and it never sends a write - every
// function here is a GET.

const TRACKING_WORKERS_PATH = "/backoffice/tracking/workers";
const ACCESS_LOGS_PATH = "/backoffice/tracking/footstep-access-logs";

// Exactly the query param names BackofficeFootstepController.GetHistory
// declares, in the same order as the controller signature - anything else
// passed to getFootstepHistory is silently dropped rather than forwarded.
const HISTORY_PARAM_KEYS = ["fromUtc", "toUtc", "assignmentId", "sampleMode", "maxPoints", "purpose", "page", "pageSize"];

// Exactly the query param names BackofficeFootstepController.GetSummary
// declares.
const SUMMARY_PARAM_KEYS = ["fromUtc", "toUtc", "assignmentId"];

// Exactly the query param names BackofficeFootstepController.ListAccessLogs
// declares. This endpoint is SuperAdmin-only on the backend - this file
// never adds a role check of its own, it only whitelists param keys the
// same way every other list endpoint in this app does.
const ACCESS_LOG_PARAM_KEYS = ["workerId", "actorUserId", "fromUtc", "toUtc", "page", "pageSize"];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

// Footstep History is sensitive-by-design: every successful call writes a
// dbo.WmxFootstepAccessLogs row on the backend (FootstepService.GetHistoryAsync).
// This function never calls the endpoint for a missing workerId - the caller
// (FootstepHistoryPage) only calls this once its own purpose/worker/range
// validation has already passed.
export function getFootstepHistory(workerId, params = {}) {
  if (!workerId) {
    throw new Error("getFootstepHistory requires a workerId");
  }
  return apiClient.get(`${TRACKING_WORKERS_PATH}/${encodeURIComponent(workerId)}/footsteps${buildQuery(HISTORY_PARAM_KEYS, params)}`);
}

// Lighter aggregate read over the same range - also writes an access-log row
// on the backend (accessType: "Summary"), but has no purpose field of its
// own (FootstepService.GetSummaryAsync never requires one).
export function getFootstepSummary(workerId, params = {}) {
  if (!workerId) {
    throw new Error("getFootstepSummary requires a workerId");
  }
  return apiClient.get(`${TRACKING_WORKERS_PATH}/${encodeURIComponent(workerId)}/footsteps/summary${buildQuery(SUMMARY_PARAM_KEYS, params)}`);
}

// SuperAdmin-only compliance review list - never includes a coordinate or a
// points list (FootstepAccessLogDto has no such field).
export function listFootstepAccessLogs(params = {}) {
  return apiClient.get(`${ACCESS_LOGS_PATH}${buildQuery(ACCESS_LOG_PARAM_KEYS, params)}`);
}
